import React from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const PlanPreview = ({ planData, onClose, onSave }) => {
  const getTotalDuration = () => {
    let totalMinutes = 0;
    
    // Calculate individual exercises
    const individualExercises = planData?.exercises?.filter(ex => !ex?.planConfig?.supersetGroup);
    totalMinutes += individualExercises?.length * 2.5; // 2.5 minutes per individual exercise
    
    // Calculate superset groups
    const supersetGroups = {};
    planData?.exercises?.forEach(exercise => {
      if (exercise?.planConfig?.supersetGroup) {
        if (!supersetGroups?.[exercise?.planConfig?.supersetGroup]) {
          supersetGroups[exercise.planConfig.supersetGroup] = [];
        }
        supersetGroups?.[exercise?.planConfig?.supersetGroup]?.push(exercise);
      }
    });
    
    Object.values(supersetGroups)?.forEach(group => {
      totalMinutes += group?.length * 2.5; // Same calculation for superset exercises
    });
    
    return Math.ceil(totalMinutes);
  };

  const getPhaseDistribution = () => {
    const distribution = { 'phase-1': 0, 'phase-2': 0, 'phase-3': 0 };
    planData?.exercises?.forEach(exercise => {
      distribution[exercise.planConfig.selectedPhase]++;
    });
    return distribution;
  };

  const getCategoryDistribution = () => {
    const distribution = {};
    planData?.exercises?.forEach(exercise => {
      distribution[exercise.category] = (distribution?.[exercise?.category] || 0) + 1;
    });
    return distribution;
  };

  const phaseDistribution = getPhaseDistribution();
  const categoryDistribution = getCategoryDistribution();
  const totalDuration = getTotalDuration();

  const categoryLabels = {
    'upper-body': 'Upper Body',
    'lower-body': 'Lower Body',
    'core': 'Core & Stability',
    'cardio': 'Cardiovascular',
    'balance': 'Balance & Coordination',
    'flexibility': 'Flexibility & Mobility'
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-surface rounded-lg shadow-elevated max-w-4xl w-full max-h-[90vh] overflow-hidden">
        {/* Header */}
        <div className="p-6 border-b border-border">
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-2xl font-bold text-foreground">{planData?.name}</h2>
              <p className="text-muted-foreground mt-1">{planData?.description}</p>
            </div>
            
            <Button
              variant="ghost"
              size="icon"
              onClick={onClose}
              className="breathing-focus"
              aria-label="Close preview"
            >
              <Icon name="X" size={24} />
            </Button>
          </div>
        </div>

        {/* Content */}
        <div className="overflow-y-auto max-h-[calc(90vh-200px)]">
          <div className="p-6">
            {/* Plan Overview */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
              <div className="bg-primary/5 border border-primary/20 rounded-lg p-4">
                <div className="flex items-center space-x-3">
                  <Icon name="Clock" size={24} className="text-primary" />
                  <div>
                    <p className="text-sm text-muted-foreground">Estimated Duration</p>
                    <p className="text-xl font-semibold text-foreground">{totalDuration} min</p>
                  </div>
                </div>
              </div>
              
              <div className="bg-secondary/5 border border-secondary/20 rounded-lg p-4">
                <div className="flex items-center space-x-3">
                  <Icon name="Target" size={24} className="text-secondary" />
                  <div>
                    <p className="text-sm text-muted-foreground">Total Exercises</p>
                    <p className="text-xl font-semibold text-foreground">{planData?.exercises?.length}</p>
                  </div>
                </div>
              </div>
              
              <div className="bg-warning/5 border border-warning/20 rounded-lg p-4">
                <div className="flex items-center space-x-3">
                  <Icon name="Link" size={24} className="text-warning" />
                  <div>
                    <p className="text-sm text-muted-foreground">Supersets</p>
                    <p className="text-xl font-semibold text-foreground">
                      {Object.keys(planData?.exercises?.reduce((groups, ex) => {
                        if (ex?.planConfig?.supersetGroup) groups[ex.planConfig.supersetGroup] = true;
                        return groups;
                      }, {}))?.length}
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* Phase Distribution */}
            <div className="mb-8">
              <h3 className="text-lg font-semibold text-foreground mb-4">Phase Distribution</h3>
              <div className="grid grid-cols-3 gap-4">
                {Object.entries(phaseDistribution)?.map(([phase, count]) => (
                  <div key={phase} className="bg-muted/30 rounded-lg p-4">
                    <div className="text-center">
                      <p className="text-2xl font-bold text-foreground">{count}</p>
                      <p className="text-sm text-muted-foreground">
                        {phase === 'phase-1' ? '2-4 reps' : 
                         phase === 'phase-2' ? '6-8 reps' : '10-12 reps'}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Category Distribution */}
            <div className="mb-8">
              <h3 className="text-lg font-semibold text-foreground mb-4">Exercise Categories</h3>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                {Object.entries(categoryDistribution)?.map(([category, count]) => (
                  <div key={category} className="flex items-center justify-between p-3 bg-muted/30 rounded-lg">
                    <span className="text-sm text-foreground">{categoryLabels?.[category] || category}</span>
                    <span className="font-semibold text-primary">{count}</span>
                  </div>
                ))}
              </div>
            </div>

            {/* Exercise List */}
            <div>
              <h3 className="text-lg font-semibold text-foreground mb-4">Exercise Sequence</h3>
              <div className="space-y-3">
                {planData?.exercises?.sort((a, b) => a?.order - b?.order)?.map((exercise, index) => (
                    <div key={exercise?.id} className="flex items-center space-x-4 p-4 bg-muted/30 rounded-lg">
                      <div className="w-8 h-8 bg-primary rounded-full flex items-center justify-center text-primary-foreground font-semibold text-sm">
                        {index + 1}
                      </div>
                      
                      <div className="flex-1">
                        <div className="flex items-center space-x-3">
                          <h4 className="font-medium text-foreground">{exercise?.name}</h4>
                          {exercise?.planConfig?.supersetGroup && (
                            <span className="px-2 py-1 bg-secondary/10 text-secondary text-xs rounded-full">
                              {exercise?.planConfig?.supersetGroup}
                            </span>
                          )}
                        </div>
                        <p className="text-sm text-muted-foreground">
                          {exercise?.equipment} • {exercise?.planConfig?.selectedPhase?.replace('-', ' ')} • 
                          Intensity: {exercise?.planConfig?.startingIntensity}%
                        </p>
                      </div>
                      
                      <div className="text-right">
                        <p className="text-sm font-medium text-foreground">
                          {exercise?.phases?.[exercise?.planConfig?.selectedPhase]?.minReps}-
                          {exercise?.phases?.[exercise?.planConfig?.selectedPhase]?.maxReps} reps
                        </p>
                        <p className="text-xs text-muted-foreground">3 sets</p>
                      </div>
                    </div>
                  ))}
              </div>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="p-6 border-t border-border bg-muted/30">
          <div className="flex items-center justify-between">
            <div className="text-sm text-muted-foreground">
              <p>Plan created on {new Date()?.toLocaleDateString()}</p>
              {planData?.assignedClient && (
                <p>Assigned to: {planData?.assignedClient}</p>
              )}
            </div>
            
            <div className="flex items-center space-x-3">
              <Button
                variant="outline"
                onClick={onClose}
                className="breathing-focus"
              >
                Close Preview
              </Button>
              <Button
                variant="default"
                onClick={onSave}
                iconName="Save"
                className="breathing-focus haptic-button"
              >
                Save Plan
              </Button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PlanPreview;